
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Role, 
  AircraftType, 
  Airstrip, 
  AdminSettings, 
  Quote, 
  Customer,
  ModuleID,
  Booking,
  UserAccount,
  Notification,
} from './types';
import { 
  INITIAL_SETTINGS, 
  MOCK_BOOKINGS,
  INITIAL_USERS
} from './constants';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import QuoteView from './components/QuoteView';
import ScheduledView from './components/ScheduledView';
import AdminPortal from './components/AdminPortal';
import AdminTools from './components/AdminTools';
import PlanningBoard from './components/PlanningBoard';
import FinanceDashboard from './components/FinanceDashboard';
import CustomerManager from './components/CustomerManager';
import ContactsHub from './components/ContactsHub';
import UserManager from './components/UserManager';
import PilotManager from './components/PilotManager';
import AIAgent from './components/AIAgent';
import GlobalWhatsAppQuickDrawer from './components/GlobalWhatsAppQuickDrawer';
import GlobalSoftPhoneDrawer from './components/GlobalSoftPhoneDrawer';
import Login from './components/Login';
import FRMSPortal from './components/FRMSPortal';
import StrategyInsights from './components/StrategyInsights';
import AuditLogView from './components/AuditLogView';
import OperationsTvBoard from './components/OperationsTvBoard';
import AeroCrsBookingDesk from './components/AeroCrsBookingDesk';
import { authApi } from './services/api';
import { fleetApi } from './services/fleetApi';
import { customerApi } from './services/customerApi';
import { contactsApi } from './services/contactsApi';
import { quoteApi } from './services/quoteApi';
import { agentService } from './services/agent';
import { adminService } from './services/adminService';
import { useFaviconState } from './hooks/useFaviconState';
import { AnnouncementBanner } from './components/AnnouncementBanner';
import { GDashScheduler } from './components/GDash/GDashScheduler';

const VALID_TABS: ModuleID[] = [
  'DASHBOARD',
  'OPERATIONS_TV',
  'STRATEGY',
  'QUOTER',
  'INBOX',
  'SCHEDULED',
  'PLANNER',
  'FRMS',
  'CUSTOMERS',
  'CONTACTS',
  'FINANCE',
  'ADMIN',
  'ADMIN_TOOLS',
  'OUTBOUND',
  'AUDIT',
  'USERS',
  'PILOTS',
  'COMMS',
  'CEO_HUB',
  'FLEET',
];
const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const IDLE_TIMEOUT_MS = 45 * 60 * 1000;
const IDLE_WARNING_SECONDS = 60;
const NO_ACCESS = { view: false, create: false, edit: false, delete: false, approve: false };
const buildDenyAllPermissions = () =>
  Object.fromEntries(VALID_TABS.map((tab) => [tab, { ...NO_ACCESS }])) as any;
const CUSTOMER_LAZY_TABS: ModuleID[] = ['CUSTOMERS', 'CONTACTS', 'QUOTER', 'SCHEDULED', 'PLANNER', 'FINANCE', 'DASHBOARD', 'STRATEGY'];
const QUOTE_LAZY_TABS: ModuleID[] = ['QUOTER', 'SCHEDULED', 'FINANCE', 'PLANNER', 'DASHBOARD', 'STRATEGY', 'FRMS', 'PILOTS'];

const isOperationsTvPath = (): boolean => /\/operations-tv\/?$/.test(window.location.pathname.toLowerCase());
const getOperationsTvAliasFromPath = (): string | null => {
  const match = window.location.pathname.toLowerCase().match(/\/otv\/([a-z0-9-]+)\/?$/);
  if (!match?.[1]) return null;
  return match[1];
};
const isOperationsTvAliasPath = (): boolean => Boolean(getOperationsTvAliasFromPath());
const isCommsTvPath = (): boolean => /\/comms-tv\/?$/.test(window.location.pathname.toLowerCase());
const getCommsTvAliasFromPath = (): string | null => {
  const match = window.location.pathname.toLowerCase().match(/\/ctv\/([a-z0-9-]+)\/?$/);
  if (!match?.[1]) return null;
  return match[1];
};
const isCommsTvAliasPath = (): boolean => Boolean(getCommsTvAliasFromPath());
const isAeroCrsDeskPath = (): boolean => /\/reservations\/aerocrs\/?$/.test(window.location.pathname.toLowerCase());
const isAeroCrsDeskHashRoute = (): boolean => window.location.hash.toLowerCase().startsWith('#/reservations/aerocrs');
const hasTvKioskQuery = (query?: string): boolean => {
  const params = new URLSearchParams(query ?? window.location.search);
  return params.get('tv') === '1' || params.get('kiosk') === '1';
};
const getHashQueryParams = (): URLSearchParams => {
  const raw = window.location.hash.replace(/^#\//, '');
  const [, queryPart = ''] = raw.split('?');
  return new URLSearchParams(queryPart);
};
const getOperationsTvPublicToken = (): string => {
  const searchParams = new URLSearchParams(window.location.search);
  const fromSearch = String(searchParams.get('token') || '').trim();
  if (fromSearch) return fromSearch;
  const hashParams = getHashQueryParams();
  return String(hashParams.get('token') || '').trim();
};
const isOperationsTvPublicMode = (): boolean => {
  const token = getOperationsTvPublicToken();
  const alias = getOperationsTvAliasFromPath();
  if (isOperationsTvAliasPath()) return true;
  if (!token) return false;
  if (isOperationsTvPath() || alias) return true;
  return parseHashRoute().tab === 'OPERATIONS_TV';
};
const getCommsTvPublicToken = (): string => {
  const searchParams = new URLSearchParams(window.location.search);
  const fromSearch = String(searchParams.get('token') || '').trim();
  if (fromSearch) return fromSearch;
  const hashParams = getHashQueryParams();
  return String(hashParams.get('token') || '').trim();
};
const getCommsTvPublicTeam = (): 'all' | 'reservations' | 'flight_planners' | null => {
  const searchParams = new URLSearchParams(window.location.search);
  const fromSearch = String(searchParams.get('team') || '').trim().toLowerCase();
  if (fromSearch === 'all' || fromSearch === 'reservations' || fromSearch === 'flight_planners') {
    return fromSearch;
  }
  const hashParams = getHashQueryParams();
  const fromHash = String(hashParams.get('team') || '').trim().toLowerCase();
  if (fromHash === 'all' || fromHash === 'reservations' || fromHash === 'flight_planners') {
    return fromHash;
  }
  return null;
};
const isCommsTvPublicMode = (): boolean => {
  const token = getCommsTvPublicToken();
  const alias = getCommsTvAliasFromPath();
  if (isCommsTvAliasPath()) return true;
  if (!token) return false;
  if (isCommsTvPath() || alias) return true;
  return parseHashRoute().tab === 'COMMS';
};

const parseHashRoute = (): { tab: ModuleID; plannerDate?: string; desktopMode: boolean; plannerMode?: 'TACTICAL' | 'CREW'; tvMode: boolean; inboxSubRoute?: string; scheduledSubRoute?: 'AEROCRS' } => {
  const raw = window.location.hash.replace(/^#\//, '');
  const [pathPart, queryPart = ''] = raw.split('?');
  const normalizedPath = String(pathPart || '').toLowerCase();
  const scheduledSubRoute = normalizedPath === 'reservations/aerocrs' ? 'AEROCRS' : undefined;
  const tab = scheduledSubRoute
    ? 'SCHEDULED'
    : (normalizedPath === 'inbox' || normalizedPath.startsWith('inbox/'))
    ? 'INBOX'
    : ((pathPart || '').toUpperCase() as ModuleID);
  const safeTab: ModuleID = VALID_TABS.includes(tab) ? tab : 'DASHBOARD';
  const inboxSubRoute = normalizedPath.startsWith('inbox/') ? normalizedPath.slice('inbox/'.length) : undefined;
  const params = new URLSearchParams(queryPart);
  const dateParam = params.get('date') || '';
  const plannerDate = DATE_KEY_RE.test(dateParam) ? dateParam : undefined;
  const desktopMode = params.get('desktop') === '1';
  const tvMode = params.get('tv') === '1' || params.get('kiosk') === '1' || hasTvKioskQuery();
  const modeParam = String(params.get('mode') || '').toUpperCase();
  const plannerMode = modeParam === 'CREW' ? 'CREW' : modeParam === 'TACTICAL' ? 'TACTICAL' : undefined;
  return { tab: safeTab, plannerDate, desktopMode, plannerMode, tvMode, inboxSubRoute, scheduledSubRoute };
};

const parseInboxOpenParams = (): { openMessageId?: string; threadId?: string } => {
  const raw = window.location.hash.replace(/^#\//, '');
  const [pathPart, queryPart = ''] = raw.split('?');
  if ((pathPart || '').toLowerCase() !== 'inbox') return {};
  const params = new URLSearchParams(queryPart);
  const openMessageId = String(params.get('openMessageId') || '').trim() || undefined;
  const threadId = String(params.get('threadId') || '').trim() || undefined;
  return { openMessageId, threadId };
};

const parsePathRoute = (): { tab: ModuleID; tvMode: boolean; commsAlias?: string | null; scheduledSubRoute?: 'AEROCRS' } | null => {
  if (window.location.hash && window.location.hash !== '#' && window.location.hash !== '#/') return null;
  if (!isOperationsTvPath() && !isOperationsTvAliasPath() && !isCommsTvPath() && !isCommsTvAliasPath() && !isAeroCrsDeskPath()) return null;
  const params = new URLSearchParams(window.location.search);
  const opsAlias = getOperationsTvAliasFromPath();
  const commsAlias = getCommsTvAliasFromPath();
  if (isAeroCrsDeskPath()) {
    return {
      tab: 'SCHEDULED',
      tvMode: false,
      scheduledSubRoute: 'AEROCRS',
    };
  }
  return {
    tab: (isOperationsTvPath() || isOperationsTvAliasPath()) ? 'OPERATIONS_TV' : 'COMMS',
    tvMode: (commsAlias || opsAlias) ? true : (params.get('tv') === '1' || params.get('kiosk') === '1'),
    commsAlias,
  };
};

const estimatePayloadBytes = (value: unknown): number => {
  try {
    return new TextEncoder().encode(JSON.stringify(value ?? null)).length;
  } catch {
    return 0;
  }
};

const formatMetricBytes = (bytes: number): string => `${(bytes / 1024).toFixed(2)} KB`;

const App: React.FC = () => {
  const publicOpsMonitorToken = getOperationsTvPublicToken();
  const publicOpsMonitorAlias = getOperationsTvAliasFromPath();
  const publicOpsMonitorMode = isOperationsTvPublicMode();
  const publicCommsMonitorToken = getCommsTvPublicToken();
  const publicCommsMonitorAlias = getCommsTvAliasFromPath();
  const publicCommsMonitorTeam = getCommsTvPublicTeam();
  const publicCommsMonitorMode = isCommsTvPublicMode();
  const publicMonitorMode = publicOpsMonitorMode || publicCommsMonitorMode;
  const publicMonitorTvMode = publicMonitorMode;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserAccount | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useFaviconState(isAuthenticated);
  
  const [activeTab, setActiveTab] = useState<ModuleID>(() => parsePathRoute()?.tab || parseHashRoute().tab);
  const [plannerLaunchDate, setPlannerLaunchDate] = useState<string | undefined>(() => parseHashRoute().plannerDate);
  const [plannerLaunchMode, setPlannerLaunchMode] = useState<'TACTICAL' | 'CREW' | undefined>(() => parseHashRoute().plannerMode);
  const [scheduledSubRoute, setScheduledSubRoute] = useState<'AEROCRS' | undefined>(() => parsePathRoute()?.scheduledSubRoute || parseHashRoute().scheduledSubRoute);
  const [operationsTvKioskMode, setOperationsTvKioskMode] = useState<boolean>(
    () => parsePathRoute()?.tvMode || parseHashRoute().tvMode || hasTvKioskQuery()
  );
  const [plannerDesktopMode, setPlannerDesktopMode] = useState<boolean>(() => {
    const parsed = parseHashRoute();
    return parsed.tab === 'PLANNER' && parsed.desktopMode;
  });

  const [settings, setSettings] = useState<AdminSettings>(INITIAL_SETTINGS);
  const [aircrafts, setAircrafts] = useState<AircraftType[]>([]);
  const [airstrips, setAirstrips] = useState<Airstrip[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [bookings, setBookings] = useState<Booking[]>(MOCK_BOOKINGS);
  const [users, setUsers] = useState<UserAccount[]>(INITIAL_USERS);
  const [inboxInitWaThreadId, setInboxInitWaThreadId] = useState<string | undefined>();
  const [inboxInitEmailSearch, setInboxInitEmailSearch] = useState<string | undefined>();
  const [inboxInitOpenMessageId, setInboxInitOpenMessageId] = useState<string | undefined>();
  const [inboxInitOpenThreadId, setInboxInitOpenThreadId] = useState<string | undefined>();
  const [inboxInitSubRoute, setInboxInitSubRoute] = useState<string | undefined>(() => parseHashRoute().inboxSubRoute);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(IDLE_WARNING_SECONDS);
  const idleTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const idleLogoutTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const idleCountdownIntervalRef = useRef<ReturnType<typeof window.setInterval> | null>(null);
  const idleWarningVisibleRef = useRef(false);
  const hasLoadedCustomersRef = useRef(false);
  const hasLoadedQuotesRef = useRef(false);
  const customersRequestRef = useRef<Promise<Customer[]> | null>(null);
  const quotesRequestRef = useRef<Promise<Quote[]> | null>(null);
  const startupMetricsRef = useRef({
    startedAt: 0,
    requestCount: 0,
    payloadBytes: 0,
  });

  // premium theme toggle state (stored in localStorage)
  const [premiumMode, setPremiumMode] = useState<boolean>(() => {
    return localStorage.getItem('ma_theme') === 'premium';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => localStorage.getItem('ma_sidebar_collapsed') === '1');
  const [inboxActiveChannel, setInboxActiveChannel] = useState<'EMAIL' | 'WHATSAPP'>(
    inboxInitWaThreadId ? 'WHATSAPP' : 'EMAIL'
  );

  // Capture token from auth callback redirects and persist before session validation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');
    if (tokenFromUrl && !publicMonitorMode) {
      localStorage.setItem('ma_jwt', tokenFromUrl);
      const cleanUrl = `${window.location.origin}${window.location.pathname}${window.location.hash}`;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [publicMonitorMode]);

  // persist theme preference whenever it changes
  useEffect(() => {
    localStorage.setItem('ma_theme', premiumMode ? 'premium' : 'default');
  }, [premiumMode]);

  useEffect(() => {
    localStorage.setItem('ma_sidebar_collapsed', sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);
  
  const beginStartupInstrumentation = useCallback(() => {
    startupMetricsRef.current = {
      startedAt: performance.now(),
      requestCount: 0,
      payloadBytes: 0,
    };
  }, []);

  const recordStartupRequest = useCallback((label: string, payload: unknown) => {
    startupMetricsRef.current.requestCount += 1;
    startupMetricsRef.current.payloadBytes += estimatePayloadBytes(payload);
    console.info('[StartupAudit]', label, {
      bytes: estimatePayloadBytes(payload),
      totalRequests: startupMetricsRef.current.requestCount,
      totalPayloadBytes: startupMetricsRef.current.payloadBytes,
    });
  }, []);

  const finalizeStartupInstrumentation = useCallback((stage: string) => {
    if (!startupMetricsRef.current.startedAt) return;
    const durationMs = performance.now() - startupMetricsRef.current.startedAt;
    const summary = {
      stage,
      durationMs: Number(durationMs.toFixed(2)),
      requestCount: startupMetricsRef.current.requestCount,
      payloadBytes: startupMetricsRef.current.payloadBytes,
      payloadLabel: formatMetricBytes(startupMetricsRef.current.payloadBytes),
    };
    (window as any).__meosStartupMetrics = summary;
    console.info('[StartupAudit] Summary', summary);
  }, []);

  const loadEnterpriseData = async () => {
    try {
      const { userService } = await import('./services/userService');
      const [acData, asData, usersData] = await Promise.all([
        fleetApi.getAircrafts().catch(() => []),
        fleetApi.getAirstrips().catch(() => []),
        userService.getAllUsers().catch(() => []),
      ]);
      recordStartupRequest('aircrafts', acData);
      recordStartupRequest('airstrips', asData);
      recordStartupRequest('users', usersData);
      setAircrafts(Array.isArray(acData) ? acData : []);
      setAirstrips(Array.isArray(asData) ? asData : []);
      setUsers(Array.isArray(usersData) ? usersData : INITIAL_USERS);
    } catch (e) {
      console.error("Critical Failure: Data fetch protocol interrupted.", e);
      setAircrafts([]);
      setAirstrips([]);
    }
  };

  const loadCustomersData = useCallback(async (reason: string) => {
    if (customersRequestRef.current) return customersRequestRef.current;
    customersRequestRef.current = (async () => {
      const startedAt = performance.now();
      let custData: Customer[] = [];
      try {
        custData = await customerApi.getAll().catch(async () => {
          try {
            return await contactsApi.getAll();
          } catch {
            return [];
          }
        });
        const normalized = Array.isArray(custData) ? custData : [];
        setCustomers(normalized);
        hasLoadedCustomersRef.current = true;
        console.info('[LazyData] customers loaded', {
          reason,
          count: normalized.length,
          durationMs: Number((performance.now() - startedAt).toFixed(2)),
          payloadBytes: estimatePayloadBytes(normalized),
        });
        return normalized;
      } catch {
        setCustomers([]);
        hasLoadedCustomersRef.current = true;
        return [];
      } finally {
        customersRequestRef.current = null;
      }
    })();
    return customersRequestRef.current;
  }, []);

  const loadQuotesData = useCallback(async (reason: string) => {
    if (quotesRequestRef.current) return quotesRequestRef.current;
    quotesRequestRef.current = (async () => {
      const startedAt = performance.now();
      try {
        const data = await quoteApi.getQuotes();
        const normalized = Array.isArray(data) ? data : [];
        setQuotes(normalized);
        hasLoadedQuotesRef.current = true;
        console.info('[LazyData] quotes loaded', {
          reason,
          count: normalized.length,
          durationMs: Number((performance.now() - startedAt).toFixed(2)),
          payloadBytes: estimatePayloadBytes(normalized),
        });
        return normalized;
      } catch {
        setQuotes([]);
        hasLoadedQuotesRef.current = true;
        return [];
      } finally {
        quotesRequestRef.current = null;
      }
    })();
    return quotesRequestRef.current;
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!CUSTOMER_LAZY_TABS.includes(activeTab)) return;
    if (hasLoadedCustomersRef.current) return;
    void loadCustomersData(`tab:${activeTab}`);
  }, [activeTab, isAuthenticated, loadCustomersData]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!QUOTE_LAZY_TABS.includes(activeTab)) return;
    if (hasLoadedQuotesRef.current) return;
    void loadQuotesData(`tab:${activeTab}`);
  }, [activeTab, isAuthenticated, loadQuotesData]);

  const loadSystemSettings = async (userRole?: Role) => {
    try {
      if (userRole === 'ADMIN') {
        const loaded = await adminService.getSettings();
        setSettings(loaded);
        return;
      }
      const runtime = await adminService.getRuntimeSelfSettings();
      setSettings(runtime.settings);
      return;
    } catch (e) {
      console.error('Failed to load system settings:', e);
      const fallbackRole = (userRole || currentUser?.role || 'RESERVATIONS') as Role;
      setSettings(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [fallbackRole]: buildDenyAllPermissions(),
        } as any,
      }));
      // Fallback to agent-only settings if full settings fail.
      try {
        const agentSettings = await agentService.getSettings();
        setSettings(prev => ({ ...prev, agent: agentSettings }));
      } catch (inner) {
        console.error('Failed to load agent settings:', inner);
      }
    }
  };

  useEffect(() => {
    if (publicMonitorMode) {
      setIsInitializing(false);
      return;
    }
    const validate = async () => {
      const token = localStorage.getItem('ma_jwt');
      if (!token) {
        setIsInitializing(false);
        return;
      }

      try {
        beginStartupInstrumentation();
        const userProfile = await authApi.validateSession();
        recordStartupRequest('auth.validateSession', userProfile);
        setIsAuthenticated(true);
        setCurrentUser(userProfile);
        // Render shell first; load heavy datasets in background.
        void loadEnterpriseData().finally(() => finalizeStartupInstrumentation('session-validate'));
        await loadSystemSettings(userProfile.role);
      } catch (err) {
        localStorage.removeItem('ma_jwt');
      } finally {
        setIsInitializing(false);
      }
    };
    validate();
  }, [publicMonitorMode]);

  useEffect(() => {
    if (activeTab === 'OPERATIONS_TV' && (isOperationsTvPath() || isOperationsTvAliasPath())) return;
    if (activeTab === 'COMMS' && (isCommsTvPath() || isCommsTvAliasPath())) return;
    if (activeTab === 'SCHEDULED' && scheduledSubRoute === 'AEROCRS' && (isAeroCrsDeskPath() || isAeroCrsDeskHashRoute())) return;
    if (plannerDesktopMode && activeTab === 'PLANNER') return;
    if (activeTab === 'INBOX' && window.location.hash.toLowerCase().startsWith('#/inbox/')) return;
    if (activeTab === 'SCHEDULED' && scheduledSubRoute === 'AEROCRS') {
      window.location.hash = '#/reservations/aerocrs';
      return;
    }
    window.location.hash = `#/${activeTab.toLowerCase()}`;
  }, [activeTab, plannerDesktopMode, scheduledSubRoute]);

  useEffect(() => {
    const onHashChange = () => {
      if (isOperationsTvPath() || isOperationsTvAliasPath() || isCommsTvPath() || isCommsTvAliasPath()) {
        const parsedPath = parsePathRoute();
        if (parsedPath) {
          setOperationsTvKioskMode(parsedPath.tvMode);
          setScheduledSubRoute(parsedPath.scheduledSubRoute);
          if (parsedPath.tab !== activeTab) setActiveTab(parsedPath.tab);
          return;
        }
      }
      if (isAeroCrsDeskPath()) {
        const parsedPath = parsePathRoute();
        if (parsedPath) {
          setScheduledSubRoute(parsedPath.scheduledSubRoute);
          setOperationsTvKioskMode(false);
          if (parsedPath.tab !== activeTab) setActiveTab(parsedPath.tab);
          return;
        }
      }
      const parsed = parseHashRoute();
      const inboxOpenParams = parseInboxOpenParams();
      setPlannerLaunchDate(parsed.tab === 'PLANNER' ? parsed.plannerDate : undefined);
      setPlannerLaunchMode(parsed.tab === 'PLANNER' ? parsed.plannerMode : undefined);
      setPlannerDesktopMode(parsed.tab === 'PLANNER' && parsed.desktopMode);
      setOperationsTvKioskMode(parsed.tab === 'OPERATIONS_TV' ? (parsed.tvMode || hasTvKioskQuery()) : false);
      setInboxInitOpenMessageId(inboxOpenParams.openMessageId);
      setInboxInitOpenThreadId(inboxOpenParams.threadId);
      setInboxInitSubRoute(parsed.inboxSubRoute);
      setScheduledSubRoute(parsed.tab === 'SCHEDULED' ? parsed.scheduledSubRoute : undefined);
      if (parsed.tab !== activeTab) setActiveTab(parsed.tab);
    };
    window.addEventListener('hashchange', onHashChange);
    onHashChange();
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [activeTab]);

  const handleLogin = async (userProfile: UserAccount, token?: string) => {
    setIsAuthenticated(true);
    setCurrentUser(userProfile);
    if (token) localStorage.setItem('ma_jwt', token);
    beginStartupInstrumentation();
    recordStartupRequest('auth.loginProfile', userProfile);
    // Avoid blocking login completion on heavy data endpoints.
    void loadEnterpriseData().finally(() => finalizeStartupInstrumentation('login'));
    await loadSystemSettings(userProfile.role);

    if (userProfile.role === 'CEO') setActiveTab('STRATEGY');
    else if (userProfile.role === 'RESERVATIONS') setActiveTab('QUOTER');
    else if (userProfile.role === 'FLIGHT_PLANNER') setActiveTab('PLANNER');
    else setActiveTab('DASHBOARD');
  };

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setShowIdleWarning(false);
    hasLoadedCustomersRef.current = false;
    hasLoadedQuotesRef.current = false;
    customersRequestRef.current = null;
    quotesRequestRef.current = null;
    setCustomers([]);
    setQuotes([]);
    localStorage.removeItem('ma_jwt');
    window.location.hash = '#/dashboard';
  }, []);

  const clearIdleTimers = useCallback(() => {
    if (idleTimeoutRef.current) {
      window.clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
    if (idleLogoutTimeoutRef.current) {
      window.clearTimeout(idleLogoutTimeoutRef.current);
      idleLogoutTimeoutRef.current = null;
    }
    if (idleCountdownIntervalRef.current) {
      window.clearInterval(idleCountdownIntervalRef.current);
      idleCountdownIntervalRef.current = null;
    }
  }, []);

  const keepSessionActive = useCallback(() => {
    clearIdleTimers();
    idleWarningVisibleRef.current = false;
    setShowIdleWarning(false);
    setIdleCountdown(IDLE_WARNING_SECONDS);
    if (isAuthenticated) {
      idleTimeoutRef.current = window.setTimeout(() => {
        idleWarningVisibleRef.current = true;
        setIdleCountdown(IDLE_WARNING_SECONDS);
        setShowIdleWarning(true);
        idleCountdownIntervalRef.current = window.setInterval(() => {
          setIdleCountdown((seconds) => Math.max(0, seconds - 1));
        }, 1000);
        idleLogoutTimeoutRef.current = window.setTimeout(() => {
          clearIdleTimers();
          idleWarningVisibleRef.current = false;
          handleLogout();
        }, IDLE_WARNING_SECONDS * 1000);
      }, IDLE_TIMEOUT_MS);
    }
  }, [clearIdleTimers, handleLogout, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearIdleTimers();
      idleWarningVisibleRef.current = false;
      setShowIdleWarning(false);
      return;
    }

    const activityEvents: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    const resetIdleTimer = () => {
      if (idleWarningVisibleRef.current) return;
      keepSessionActive();
    };

    keepSessionActive();
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetIdleTimer, { passive: true });
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetIdleTimer);
      });
      clearIdleTimers();
    };
  }, [clearIdleTimers, isAuthenticated, keepSessionActive]);

  if (isInitializing) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-slate-900 text-white font-sans">
        <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-6"></div>
        <p className="text-[10px] font-black uppercase tracking-[0.5em] animate-pulse">Establishing Secure Uplink...</p>
      </div>
    );
  }

  if (publicOpsMonitorMode) {
    return (
      <div className="h-screen w-screen overflow-hidden bg-[#020817] text-white">
        <OperationsTvBoard tvMode={publicMonitorTvMode} publicToken={publicOpsMonitorToken} publicAlias={publicOpsMonitorAlias} />
      </div>
    );
  }

  if (publicCommsMonitorMode) {
    const ReservationWallLite = React.lazy(() => import('./components/ReservationWallLite'));
    return (
      <div className="h-screen w-screen overflow-hidden bg-[#020817] text-white">
        <React.Suspense fallback={<div className="p-12 text-slate-500">Loading Reservation Wall Lite...</div>}>
          <ReservationWallLite
            publicToken={publicCommsMonitorToken}
            publicAlias={publicCommsMonitorAlias}
            publicTeam={publicCommsMonitorTeam}
          />
        </React.Suspense>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Login 
        onLogin={handleLogin}
        onSSOLogin={handleLogin} 
      />
    );
  }

  const openPlannerDesktop = (dateKey: string, mode: 'TACTICAL' | 'CREW' = 'TACTICAL') => {
    const safeDate = DATE_KEY_RE.test(dateKey)
      ? dateKey
      : new Date().toISOString().slice(0, 10);
    const modeParam = mode === 'CREW' ? '&mode=crew' : '&mode=tactical';
    window.location.hash = `#/planner?date=${encodeURIComponent(safeDate)}&desktop=1${modeParam}`;
  };

  const openDesktopCrewWorkspace = (dateKey: string) => {
    openPlannerDesktop(dateKey, 'CREW');
  };

  const exitDesktopWorkspace = (dateKey?: string) => {
    const safeDate = DATE_KEY_RE.test(String(dateKey || ''))
      ? String(dateKey)
      : new Date().toISOString().slice(0, 10);
    setActiveTab('PLANNER');
    setPlannerDesktopMode(false);
    setPlannerLaunchMode(undefined);
    setPlannerLaunchDate(safeDate);
    window.location.hash = `#/planner?date=${encodeURIComponent(safeDate)}`;
  };

  const finishDesktopWorkspace = () => {
    window.location.hash = '#/dashboard';
  };

  const openInboxWhatsAppThread = (threadId?: string) => {
    setInboxInitWaThreadId(threadId || undefined);
    setInboxInitEmailSearch(undefined);
    setActiveTab('INBOX');
    window.location.hash = '#/inbox';
  };

  const renderView = () => {
    const role = currentUser?.role || 'RESERVATIONS';
    const rolePerms = (settings.permissions as any)?.[role] || {};
    const canView = (module: ModuleID) => {
      if (module === 'OPERATIONS_TV') return rolePerms?.[module]?.view ?? true;
      return !!rolePerms?.[module]?.view;
    };

    const routePermissionMap: Partial<Record<ModuleID, ModuleID>> = {
      DASHBOARD: 'DASHBOARD',
      QUOTER: 'QUOTER',
      INBOX: 'INBOX',
      SCHEDULED: 'SCHEDULED',
      OPERATIONS_TV: 'OPERATIONS_TV',
      PLANNER: 'PLANNER',
      FRMS: 'FRMS',
      CUSTOMERS: 'CUSTOMERS',
      CONTACTS: 'CONTACTS',
      FINANCE: 'FINANCE',
      ADMIN: 'ADMIN',
      ADMIN_TOOLS: 'ADMIN_TOOLS',
      OUTBOUND: 'OUTBOUND',
      AUDIT: 'AUDIT',
      USERS: 'USERS',
      PILOTS: 'PILOTS',
      STRATEGY: 'STRATEGY',
    };

    const requiredModule = routePermissionMap[activeTab];
    if (requiredModule && !canView(requiredModule)) {
      return (
        <div className="p-12 bg-white border border-slate-100 rounded-3xl shadow-sm">
          <p className="text-sm font-black text-rose-500 uppercase tracking-[0.3em]">Access Restricted</p>
          <p className="mt-3 text-base font-semibold text-slate-700">
            Your role does not have permission to open this module.
          </p>
        </div>
      );
    }

    switch (activeTab) {
      case 'DASHBOARD': return <Dashboard currentRole={role} aircrafts={aircrafts} quotes={quotes} customers={customers} settings={settings} premiumMode={premiumMode} />;
      case 'OPERATIONS_TV': return <OperationsTvBoard tvMode={operationsTvKioskMode} publicToken={null} />;
      case 'STRATEGY': return <StrategyInsights quotes={quotes} aircrafts={aircrafts} customers={customers} users={users} currentRole={role} />;
      case 'PLANNER': return <PlanningBoard quotes={quotes} bookings={bookings} currentRole={role} customers={customers} aircrafts={aircrafts} settings={settings} initialSelectedDate={plannerLaunchDate} initialViewMode={plannerLaunchMode} isDesktopWorkspace={plannerDesktopMode} onOpenDesktopWorkspace={(dateKey) => openPlannerDesktop(dateKey, 'TACTICAL')} onOpenDesktopCrewWorkspace={openDesktopCrewWorkspace} onExitDesktopWorkspace={exitDesktopWorkspace} onFinishDesktopWorkspace={finishDesktopWorkspace} onUpdateQuote={async (id, up) => {
        const updated = await quoteApi.updateQuote(id, up);
        setQuotes(q => q.map(x => x.id === id ? updated : x));
      }} onUpdateAircraft={async (id, up) => {
        const updated = await fleetApi.updateAircraft(id, up);
        setAircrafts(a => a.map(x => x.id === id ? updated : x));
      }} users={users} />;
      case 'PILOTS': return <PilotManager users={users} setUsers={setUsers} quotes={quotes} />;
      case 'FRMS': return <FRMSPortal quotes={quotes} aircrafts={aircrafts} currentRole={role} onUpdateQuote={async (id, up) => {
        const updated = await quoteApi.updateQuote(id, up);
        setQuotes(q => q.map(x => x.id === id ? updated : x));
      }} />;
      case 'QUOTER': return <QuoteView aircrafts={aircrafts} airstrips={airstrips} customers={customers} settings={settings} quotes={quotes} setQuotes={setQuotes} currentRole={role} currentUser={currentUser} premiumMode={premiumMode} />;
      case 'INBOX': {
        const InboxHub = React.lazy(() => import('./components/InboxHub'));
        return (
          <React.Suspense fallback={<div className="p-12 text-slate-500">Loading Inbound Hub...</div>}>
            <InboxHub currentUser={currentUser} currentRole={role} settings={settings} agentSettings={settings.agent} initialWaThreadId={inboxInitWaThreadId} initialEmailSearch={inboxInitEmailSearch} initialOpenMessageId={inboxInitOpenMessageId} initialOpenThreadId={inboxInitOpenThreadId} initialSubRoute={inboxInitSubRoute} premiumMode={premiumMode} onPremiumToggle={() => setPremiumMode(m => !m)} onSignOut={handleLogout} onCollapseSidebar={() => setSidebarCollapsed(true)} onActiveChannelChange={setInboxActiveChannel} />
          </React.Suspense>
        );
      }
      case 'SCHEDULED':
        if (scheduledSubRoute === 'AEROCRS') return <AeroCrsBookingDesk currentRole={role} />;
        return <ScheduledView customers={customers} aircrafts={aircrafts} quotes={quotes} setQuotes={setQuotes} bookings={bookings} setBookings={setBookings} airstrips={airstrips} currentRole={role} currentUser={currentUser} settings={settings} />;
      case 'FINANCE': return <FinanceDashboard customers={customers} quotes={quotes} onSyncZoho={() => {}} isSyncing={false} />;
      case 'CUSTOMERS': return <CustomerManager customers={customers} setCustomers={setCustomers} quotes={quotes} settings={settings} isSyncing={false} />;
      case 'CONTACTS': return <ContactsHub
        customers={customers}
        setCustomers={setCustomers}
        onNavigateToQuote={(id: string) => { window.location.hash = `#/QUOTER?quote=${id}`; }}
        onNavigateToWA={(threadId: string) => { setInboxInitWaThreadId(threadId); setInboxInitEmailSearch(undefined); setActiveTab('INBOX'); }}
        onNavigateToEmail={(search: string) => { setInboxInitEmailSearch(search); setInboxInitWaThreadId(undefined); setActiveTab('INBOX'); }}
      />;
      case 'USERS': return <UserManager />;
      case 'ADMIN': return <AdminPortal settings={settings} setSettings={setSettings} aircrafts={aircrafts} setAircrafts={setAircrafts} airstrips={airstrips} setAirstrips={setAirstrips} />;
      case 'ADMIN_TOOLS': return <AdminTools settings={settings} setSettings={setSettings} />;
      case 'OUTBOUND': {
        const OutboundHub = React.lazy(() => import('./components/OutboundHub'));
        return (
          <React.Suspense fallback={<div className="p-12 text-slate-500">Loading Outbound Hub...</div>}>
            <OutboundHub settings={settings} setSettings={setSettings} currentRole={role} />
          </React.Suspense>
        );
      }
      case 'AUDIT': return <AuditLogView currentRole={role} />;
      case 'COMMS': {
        const CommsDashboard = React.lazy(() => import('./components/CommsDashboard'));
        return (
          <React.Suspense fallback={<div className="p-12 text-slate-500">Loading...</div>}>
            <CommsDashboard />
          </React.Suspense>
        );
      }
      case 'CEO_HUB': {
        const CeoHub = React.lazy(() => import('./components/CeoHub'));
        return (
          <React.Suspense fallback={<div className="p-12 text-slate-500">Loading CEO Hub...</div>}>
            <CeoHub currentUser={currentUser} currentRole={role as any} allUsers={users} />
          </React.Suspense>
        );
      }
      default: return <Dashboard currentRole={role} aircrafts={aircrafts} quotes={quotes} customers={customers} settings={settings} />;
    }
  };

  const agentModuleMap: Record<ModuleID, 'QUOTER' | 'SCHEDULED' | 'PLANNER' | 'FUEL' | 'WHATSAPP' | 'EMAIL' | null> = {
    DASHBOARD: null,
    OPERATIONS_TV: null,
    STRATEGY: null,
    QUOTER: 'QUOTER',
    INBOX: 'EMAIL',
    SCHEDULED: 'SCHEDULED',
    PLANNER: 'PLANNER',
    FRMS: null,
    FUEL: 'FUEL',
    CUSTOMERS: null,
    CONTACTS: null,
    FINANCE: null,
    ADMIN: null,
    ADMIN_TOOLS: null,
    OUTBOUND: null,
    USERS: null,
    PILOTS: null,
    AUDIT: null,
    FLEET: 'QUOTER',
    COMMS: null,
    CEO_HUB: null,
  };
  const activeAgentModule = agentModuleMap[activeTab];
  const appHeaderTitle = activeTab === 'SCHEDULED' && scheduledSubRoute === 'AEROCRS'
    ? 'AEROCRS Booking Desk'
    : activeTab.replace('_', ' ');
  const plannerDesktopActive = plannerDesktopMode && activeTab === 'PLANNER';
  const operationsTvKioskActive = activeTab === 'OPERATIONS_TV' && (operationsTvKioskMode || hasTvKioskQuery());
  const hideShellChrome = plannerDesktopActive || operationsTvKioskActive;
  const agentEnabledForModule = settings.agent?.enabled
    && activeAgentModule
    && settings.agent.modules[activeAgentModule]
    && settings.agent.provider?.type !== 'NONE'
    && (settings.agent.rolesByModule?.[activeAgentModule] || []).includes(currentUser?.role || 'RESERVATIONS');

  return (
    <div
      className={`flex h-screen w-full min-w-0 font-sans ${hideShellChrome ? 'bg-[#020817] text-white' : premiumMode ? 'premium bg-gradient-to-r from-slate-900 to-slate-950 text-white' : 'bg-[#fafbfd] text-slate-900'}`}
    >
      {!hideShellChrome && (
        <Sidebar
          currentRole={currentUser?.role || 'RESERVATIONS'}
          onRoleChange={
            currentUser?.role === 'ADMIN'
              ? (r) => currentUser && setCurrentUser({ ...currentUser, role: r })
              : undefined
          }
          canSwitchRoles={currentUser?.role === 'ADMIN'}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          settings={settings}
          premiumMode={premiumMode}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed(prev => !prev)}
        />
      )}

      <main className="flex-1 min-w-0 flex flex-col h-full overflow-hidden">
        {!hideShellChrome && activeTab !== 'INBOX' && (
        <header
          className={`h-[72px] border-b flex items-center justify-between px-10 shrink-0 z-50 ${
            premiumMode ? 'bg-[linear-gradient(90deg,#0c1b37_0%,#0b203f_55%,#0a1630_100%)] border-amber-300/25 shadow-[0_8px_30px_rgba(2,6,23,0.45)]' : 'bg-white border-slate-200'
          }`}
        >
          <div className="flex flex-col">
            <h1
              className={`text-xl font-extrabold tracking-tight leading-none mb-1 uppercase ${
                premiumMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              {appHeaderTitle}
            </h1>
            <nav
              className={`text-[9px] uppercase tracking-[0.25em] font-black flex items-center gap-2 ${
                premiumMode ? 'text-amber-300' : 'text-indigo-500'
              }`}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live Operational Node
            </nav>
          </div>

          <div className="flex items-center gap-6">
            <button
              onClick={() => setPremiumMode((m) => !m)}
              className={`rounded-full border px-3 py-1 text-[11px] font-black tracking-[0.12em] uppercase transition ${
                premiumMode ? 'border-amber-300/45 bg-amber-300/10 text-amber-300 hover:bg-amber-300/20 hover:text-amber-100' : 'border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {premiumMode ? 'Default Mode' : 'Premium Mode'}
            </button>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className={`text-xs font-black leading-none mb-0.5 ${premiumMode ? 'text-slate-200' : 'text-slate-800'}`}>
                  {currentUser?.name}
                </p>
                <button
                  onClick={handleLogout}
                  className={`text-[9px] font-bold uppercase tracking-tighter hover:underline ${
                    premiumMode ? 'text-amber-300' : 'text-rose-500'
                  }`}
                >
                  Sign Out
                </button>
              </div>
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-base shadow-lg uppercase overflow-hidden border ${
                  premiumMode ? 'bg-[#0b1a35] border-amber-300/30 text-white' : 'bg-slate-100 text-slate-700 border-slate-200'
                }`}
              >
                  {currentUser?.avatar && currentUser.avatar.trim() !== '' ? (
                    <img 
                      src={currentUser.avatar} 
                      className="w-full h-full object-cover" 
                      alt="User Profile"
                      onError={(e) => {
                        console.error('Profile picture failed to load:', currentUser.avatar);
                        // Fallback to initials if image fails to load
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.parentElement?.querySelector('.avatar-fallback') as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                      onLoad={() => {
                        console.log('Profile picture loaded successfully');
                      }}
                    />
                  ) : null}
                  <div className="avatar-fallback w-full h-full flex items-center justify-center" style={{ display: (currentUser?.avatar && currentUser.avatar.trim() !== '') ? 'none' : 'flex' }}>
                    {currentUser?.name ? (
                      <span>{currentUser.name.charAt(0).toUpperCase()}</span>
                    ) : currentUser?.firstName ? (
                      <span>{currentUser.firstName.charAt(0).toUpperCase()}</span>
                    ) : currentUser?.email ? (
                      <span>{currentUser.email.charAt(0).toUpperCase()}</span>
                    ) : (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                    )}
                  </div>
                </div>
              </div>
          </div>
        </header>
        )}

        {isAuthenticated && currentUser && !hideShellChrome && (
          <AnnouncementBanner currentUserId={currentUser.id ?? ''} />
        )}
        {isAuthenticated && currentUser && !publicMonitorMode ? (
          <GDashScheduler
            userId={String(currentUser.id || '')}
            isAuthenticated={isAuthenticated}
            onOpenInbox={async (item) => {
              (window as any).__gdashSuppressCloseDetailUntil = Date.now() + 3500;
              setActiveTab('INBOX');
              const messageId = String(item?.emailMessageId || item?.latestInboundMessageId || item?.messageId || '').trim();
              const threadId = String(item?.threadId || '').trim();
              const openToken = String((item as any)?.openToken || `gdash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
              try {
                const intent = { messageId, threadId, openToken, at: Date.now() };
                window.localStorage.setItem('gdash.open.intent', JSON.stringify(intent));
                window.dispatchEvent(new CustomEvent('gdash:open-intent', { detail: intent }));
              } catch {
                // ignore storage/event errors
              }
              if (!messageId && !threadId) {
                try {
                  window.localStorage.removeItem('gdash.open.intent');
                } catch {
                  // ignore
                }
                window.location.hash = '#/inbox';
                return true;
              }
              const params = new URLSearchParams();
              if (messageId) params.set('openMessageId', messageId);
              if (threadId) params.set('threadId', threadId);
              window.location.hash = `#/inbox?${params.toString()}`;
              // Return success immediately after dispatching navigation/intent.
              // InboxHub continues direct-open in background; popup should not block UI.
              return true;
            }}
          />
        ) : null}
        <div
          className={`flex-1 min-w-0 ${
            operationsTvKioskActive
              ? 'h-full overflow-hidden p-0 bg-[#020817]'
              : hideShellChrome
                ? 'overflow-y-auto custom-scrollbar p-4 bg-[#020817]'
                : activeTab === 'DASHBOARD'
                  ? `overflow-hidden p-0 ${premiumMode ? 'bg-slate-900' : 'bg-[#fafbfd]'}`
                  : activeTab === 'INBOX'
                    ? `overflow-hidden p-3 ${premiumMode ? 'bg-slate-900' : 'bg-[#fafbfd]'}`
                    : `overflow-y-auto custom-scrollbar p-10 ${premiumMode ? 'bg-slate-900' : 'bg-[#fafbfd]'}`
          }`}
        >
          {renderView()}
        </div>
      </main>

      {!hideShellChrome && agentEnabledForModule && (
        <AIAgent
          context={`User: ${currentUser?.name}\nRole: ${currentUser?.role}\nTab: ${activeTab}\nOperationalState: ${JSON.stringify({fleet: aircrafts.length, quotes: quotes.length, customers: customers.length})}`}
          agentSettings={settings.agent}
          module={activeAgentModule || undefined}
        />
      )}
      {!hideShellChrome ? (
        <GlobalWhatsAppQuickDrawer
          currentUser={currentUser}
          currentRole={currentUser?.role || 'RESERVATIONS'}
          activeTab={activeTab}
          suppressBackgroundWhatsApp={activeTab === 'INBOX' && inboxActiveChannel === 'WHATSAPP'}
          onOpenFullHub={openInboxWhatsAppThread}
        />
      ) : null}
      {!hideShellChrome ? (
        <GlobalSoftPhoneDrawer
          currentUser={currentUser}
          currentRole={currentUser?.role || 'RESERVATIONS'}
          activeTab={activeTab}
        />
      ) : null}
      {showIdleWarning ? (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-2xl">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-amber-600">Session Check</p>
            <h2 className="mt-3 text-2xl font-black text-slate-950">Still working?</h2>
            <p className="mt-3 text-sm font-semibold leading-6 text-slate-600">
              You have been inactive for 45 minutes. Your session will be closed in{' '}
              <span className="font-black text-rose-600">{idleCountdown}</span> seconds unless you stay logged in.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleLogout}
                className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-black uppercase tracking-[0.12em] text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
              >
                Log Out
              </button>
              <button
                type="button"
                onClick={keepSessionActive}
                className="rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-black uppercase tracking-[0.12em] text-white shadow-lg transition hover:bg-slate-800"
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default App;
